import type { SystemResourcesTranslationMessages } from './systemResourcesTranslationMessages';

export const systemResourcesLanguageEnglish: SystemResourcesTranslationMessages = {
  domains: {
    name: 'Domains',
    helperTexts: {
      assignDomainInput: 'Domain where this record should be assigned',
      assignDomainArrayInput: 'List of domains where this record should be assigned',
      organization: 'The organization name',
      realm: 'The SIP domain',
      domainId: 'Domain id',
      license: 'Domain License',
      altnames: {
        alias: 'A domain/host alias',
      },
      metadata: {
        openai_api_key: 'API key required to access OpenAI APIs',
        ms_contact_sync: {
          sync_config: {
            rule_value_list: 'Separate multiple values with commas',
          },
        },
      },
    },
    notifications: {
      invalidRealm: 'Invalid domain format',
      invalidMicrosoftSyncRule: 'Complete each rule or remove it',
    },
    fields: {
      fileUploadHint: 'Drop a .tar.gz backup file here, or click to browse',
      id: 'ID',
      assigned_sip_profile: 'Sip Profile',
      labels: { id: 'Labels', name: 'Labels' },
      altnames: 'Aliases',
      enabled: 'Enabled',
      enabled_features: 'Enabled Features',
      language: 'Language',
      license: 'License',
      organization: 'Organization',
      realm: 'Realm',
      metadata: {
        openai_api_key: 'OpenAI API Key',
        obfuscated_openai_api_key: 'OpenAI API Key (Obfuscated)',
        ms_contact_sync: {
          enabled: 'Microsoft Contact Sync Enabled',
          microsoft_config: {
            client_id: 'Client ID',
            client_secret: 'Client Secret',
            obfuscated_client_secret: 'Client Secret (Obfuscated)',
            realm: 'Realm',
            tenant_id: 'Tenant ID',
          },
          sync_config: {
            active: 'Active',
            add_rule: 'Add rule',
            contacts_filter: 'Contacts Filter',
            enum_active: {
              any: 'Any',
              false: 'Inactive',
              true: 'Active',
            },
            enum_operator: {
              eq: 'Equals',
              in: 'In list',
            },
            enum_type: {
              all: 'All',
              contacts: 'Contacts',
              users: 'Users',
            },
            ms_id_field: 'MS ID Field',
            public_contacts_owner: 'Public Contacts Owner',
            rule_field: 'Field',
            rule_operator: 'Operator',
            rule_value: 'Value',
            type: 'Sync Type',
            users_filter: 'Users Filter',
          },
        },
      },
    },
    operations: {
      edit: 'Edit domain',
      create: 'Create domain',
      delete: 'Delete domain',
      sections: {
        settings: 'Domain configuration',
        metadata: 'Metadata',
        microsoftSync: 'Microsoft Sync',
        license: 'License',
        backup: 'Backup',
      },
    },
    license: {
      serial: 'Serial',
      version: 'Version',
      tenant: 'Tenant',
      issued_at: 'Issued At',
      expiry: 'Expiry',
      resources: 'Resources',
      users: 'Users',
      extensions: 'Extensions',
      queues: 'Queues',
      carriers: 'Carriers',
      ivrs: 'IVRs',
      voicemails: 'Voicemails',
      api_connectors: 'API Connectors',
      auth_providers: 'Auth Providers',
      domains: 'Domains',
      features: 'User Features',
      features_queues: 'Queue Features',
      agents: 'Agents',
      totp: 'TOTP',
      softphone_base: 'Softphone Base',
      softphone_premium: 'Softphone Premium',
      statistics: 'Statistics',
      callback: 'Callback',
      expiry_resources: 'Resource Expiry',
      expiry_users: 'Users Expiry',
      expiry_queues: 'Queues Expiry',
      metered_resources: 'Metered Resources',
      ai_calls_limit: 'AI Calls Limit',
      ai_calls_reset_period: 'Reset Period',
      error: 'Error loading license',
      noLicense: 'No license associated with this domain',
      yes: 'Yes',
      no: 'No',
    },
    backup: {
      startBackup: 'Start Backup',
      success: 'Backup task started',
      error: 'Error starting backup',
      button: 'Import / Restore Backup',
      importTitle: 'Import Domain Backup',
      restoreTitle: 'Restore Domain Backup',
      importMode: 'Import',
      importModeDescription: 'Creates a new domain from the backup archive.',
      restoreMode: 'Restore',
      restoreModeDescription: 'Overwrites an existing domain with the backup archive.',
      realmOverride: 'New realm (optional)',
      targetDomain: 'Target domain',
      importSuccess: 'Backup import task started',
      restoreSuccess: 'Backup restore task started',
      importError: 'Error starting backup import',
      restoreError: 'Error starting backup restore',
      apply: 'Apply',
    },
  },
  lcrs: {
    name: 'Least Cost Routing',
    fields: {
      id: 'ID',
      description: 'Description',
      name: 'Name',
      rules: {
        id: 'ID',
        description: 'Description',
        priority: 'Priority',
        enabled: 'Enabled',
        assigned_carriers: 'Trunk',
        dialingcode: 'Dialing code',
        matchrule: 'Match rule',
      },
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create LCR',
      edit: 'Edit LCR',
      delete: 'Delete LCR',
      sections: {
        details: 'Details',
        rules: 'Rules',
      },
    },
    helperTexts: {
      rules: {
        assigned_carriers: 'A collection of Assigned carriers',
        description: 'Random notes for the rule',
        dialingcode: 'Prefix to match and strip from called number',
        matchrule: 'A regular expression to match against.',
        priority: 'Rule priority, lowest wins',
      },
    },
  },
  carriers: {
    name: 'SIP Trunk',
    fields: {
      id: 'ID',
      enabled: 'Enabled',
      description: 'Description',
      name: 'Name',
      mappings: 'Mappings',
      parameters: {
        password: 'SIP password',
        username: 'SIP username',
        realm: 'Realm',
        enable_reg: 'Enable registration',
        expires: 'Registration duration',
        extension: 'Extension number',
        host: 'Host',
        transport: 'Transport',
        from_user: 'From user',
        from_domain: 'From Domain',
        options_ping: 'SIP Options ping',
        callerid_in_from: 'Use callerid in From',
      },
      assigned_sip_profile: 'SIP Profile',
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create trunk',
      edit: 'Edit trunk',
      delete: 'Delete trunk',
      sections: {
        details: 'Details',
        parameters: 'Parameters',
      },
    },
    helperTexts: {
      mappings: 'A collection of carrier mappings',
      parameters: {
        host: 'Remote endpoint / Registrar',
        username: 'Authentication username, used also as from user if from_user is not set',
        password: 'Authentication password',
        realm: 'Authentication realm, same as host if not set',
        extension: 'Registration name on remote endpoint, same as username if not set',
        from_user: 'Username to use in from, same as username, if not set.',
        from_domain: 'Domain to use in from, same as realm if set or host.',
        transport: 'Transport protocol',
        expires: 'Registration expire timer',
        enable_reg: 'Enable remote registration to the endpoint',
        options_ping: 'Enable endpoint monitoring via SIP OPTIONS pings',
        callerid_in_from: 'Set SIP From header using the caller id number',
      },
    },
  },
  sip_profiles: {
    name: 'SIP Profiles',
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      '100rel': '100rel',
      inbound_codecs: 'Inbound Codecs',
      outbound_codecs: 'Outbound Codecs',
      session_timers: 'Session Timers',
      ice: 'ICE',
      rtp_profile: 'RTP profile',
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create profile',
      edit: 'Edit profile',
      delete: 'Delete profile',
      sections: {
        details: 'Details',
        codecs: 'Codec',
      },
    },
    helperTexts: {
      '100rel': 'Enable rfc3262 support',
      session_timers: 'Enable SIP session timers support',
      ice: 'ICE support. Required for WebRTC clients. May be unsupported by some phones or carriers.',
      rtp_profile:
        'RTP profile to use. RTP/SAVP(F) profiles are encrypted. Does not affect WSS/WebRTC clients (for which UDP/TLS/RTP/SAVPF is forced).',
    },
  },
  ap_settings: {
    name: 'Settings',
  },
  ap_authentication: {
    name: 'Authentication',
  },
  ap_configurations: {
    name: 'Configurations',
    srtpValues: {
      disabled: 'Disabled',
      optional: 'Optional',
      compulsory: 'Compulsory',
    },
    helperTexts: {
      display_name:
        'Use double curly braces for placeholders. Available variables: {{lastname}}, {{firstname}}, {{extension}}.',
    },
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      labels: { id: 'Labels', name: 'Labels' },
      settings: {
        admin_password: 'Admin password',
        ca_url: 'CA URL',
        call_waiting: 'Call waiting',
        codecs: 'Codecs',
        display_method: 'Display method',
        display_name: 'Display name',
        dscp_enable: 'Enable DSCP',
        dscp_signal: 'DSCP signal',
        dscp_voice: 'DSCP voice',
        fw_url: 'Firmware URL',
        ldap_base_search: 'LDAP base search',
        ldap_filter_name: 'LDAP filter name',
        ldap_filter_number: 'LDAP filter number',
        ldap_host: 'LDAP host',
        ldap_port: 'LDAP port',
        ldap_title: 'LDAP title',
        mcast_addr: 'Multicast addresses',
        mcast_label: 'Multicast labels',
        mwi_voicemail_number: 'MWI voicemail number',
        mwi_voicemail_subscribe: 'MWI voicemail subscribe',
        ntp_server_1: 'NTP server 1',
        ntp_server_2: 'NTP server 2',
        outbound_backup_proxy_port: 'Backup outbound proxy server SIP port',
        outbound_proxy_port: 'Outbound proxy server SIP port',
        reg_expiry: 'Registration expiry',
        sip_port: 'SIP port',
        sip_transport: 'SIP transport',
        srtp: 'SRTP',
        vlan_pcport_enable: 'Enable PC port VLAN',
        vlan_pcport_vid: 'PC port VLAN ID',
        vlan_wanport_enable: 'Enable WAN port VLAN',
        vlan_wanport_vid: 'WAN port VLAN ID',
      },
    },
    groups: {
      details: 'Details',
      labels: 'Labels',
      sip_parameters: 'SIP Parameters',
      voicemail_parameters: 'Voicemail Parameters',
      ntp_parameters: 'NTP Parameters',
      generic_parameters: 'Generic Parameters',
      vlan_parameters: 'VLAN Parameters',
      dscp: 'DSCP',
      multicast_addresses: 'Multicast Addresses',
      dsp_parameters: 'DSP Parameters',
      firmware: 'Firmware',
      ldap_parameters: 'LDAP Parameters',
    },
    operations: {
      create: 'Create configuration',
      delete: 'Delete configuration',
      edit: 'Edit configuration',
      sections: {
        details: 'Details',
        sip: 'SIP',
        device: 'Device',
        media: 'Media',
        directory: 'Directory',
        network: 'Network',
      },
    },
  },
  ap_groups: {
    name: 'Groups',
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      ap_configuration: 'Autoprovisioning configuration',
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create group',
      delete: 'Delete group',
      edit: 'Edit group',
      sections: {
        details: 'Details',
      },
    },
  },
  ap_templates: {
    name: 'Templates',
    fields: {
      id: 'ID',
      name: 'Name',
      description: 'Description',
      markup: 'Markup',
      metadata: {
        mac_regex: 'MAC regex',
        max_accounts: 'Max accounts',
        ua_patterns: 'User-Agent patterns',
      },
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create template',
      delete: 'Delete template',
      edit: 'Edit template',
      sections: {
        details: 'Details',
        metadata: 'Metadata',
      },
    },
    defaultTemplate: {
      button: 'Load Default Template',
      dialogTitle: 'Load Default Template',
      selectLabel: 'Template',
      warning: 'This will overwrite the current content of the markup field.',
    },
    helperTexts: {
      ua_patterns: 'Regular expression matched against the device User-Agent during provisioning',
    },
    validation: {
      invalid_regex: 'Must be a valid regular expression',
    },
  },
  sip_devices: {
    name: 'SIP Devices',
    fields: {
      id: 'ID',
      mac_address: 'MAC address',
      description: 'Description',
      ap_group: 'Autoprovisioning group',
      labels: { id: 'Labels', name: 'Labels' },
      sip_identities: 'SIP identities',
      provisioning_last_successful_at: 'Last successful provisioning',
      provisioning_user_agent: 'Provisioning user agent',
    },
    operations: {
      create: 'Create SIP device',
      delete: 'Delete SIP device',
      edit: 'Edit SIP device',
      sections: {
        details: 'Details',
      },
    },
  },
  inbound_e164s: {
    name: 'Inbound E164s',
    fields: {
      id: 'ID',
      description: 'Description',
      e164_match: 'E164 Match',
      labels: { id: 'Labels', name: 'Labels' },
    },
    operations: {
      create: 'Create inbound E164',
      delete: 'Delete inbound E164',
      edit: 'Edit inbound E164',
      sections: {
        settings: 'Inbound E164 configuration',
      },
    },
  },
};
